import { useState } from "react";
import { Button } from "@/components/ui/button";
import { ClipboardList, ChevronUp, ChevronDown, ChevronRight, Download } from "lucide-react";
import { formatMoney } from "@/lib/format";

export default function DeletionLog({
  showLog,
  setShowLog,
  filteredLog,
  groupedLog,
  query,
  downloadLogCsv,
  fmtDay,
  fmtTime,
}) {
  // Pro Tag ein- und ausklappbarer Ordner (wie bei den Belegen), standardmäßig geschlossen.
  const [openDays, setOpenDays] = useState({});
  return (
    <div className="bg-white border border-border overflow-hidden">
      <button
        type="button"
        onClick={() => setShowLog((v) => !v)}
        className="w-full flex items-center gap-2 text-lg font-medium text-left px-4 py-3"
        data-testid="toggle-deletion-log"
        aria-expanded={showLog}
      >
        <ClipboardList className="h-5 w-5 text-primary" />
        Lösch-Protokoll
        <span className="text-sm font-normal text-muted-foreground">({filteredLog.length})</span>
        {showLog ? (
          <ChevronUp className="h-4 w-4 ml-auto text-muted-foreground" />
        ) : (
          <ChevronDown className="h-4 w-4 ml-auto text-muted-foreground" />
        )}
      </button>

      {showLog && (
        <div className="space-y-4 border-t border-border p-4">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:justify-between">
            <p className="text-sm text-muted-foreground" data-testid="log-filter-hint">
              {query.trim()
                ? `Gefiltert nach „${query.trim()}" (${filteredLog.length})`
                : "Nutzen Sie das Suchfeld oben, um das Protokoll nach Kunde zu filtern."}
            </p>
            <Button
              variant="outline"
              onClick={() => downloadLogCsv()}
              className="rounded-none shrink-0"
              data-testid="log-download-all"
              disabled={filteredLog.length === 0}
            >
              <Download className="h-4 w-4 mr-2" />
              Protokoll als CSV
            </Button>
          </div>

          {groupedLog.length === 0 ? (
            <div
              className="bg-white border border-border py-10 text-center text-muted-foreground"
              data-testid="log-empty"
            >
              Keine Lösch-Vorgänge protokolliert.
            </div>
          ) : (
            groupedLog.map(([day, entries]) => {
              const isOpen = !!openDays[day];
              return (
                <div key={day} className="bg-white border border-border" data-testid={`log-day-${day}`}>
                  <div
                    className={`flex items-center justify-between px-4 py-2.5 bg-muted/30 ${
                      isOpen ? "border-b border-border" : ""
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => setOpenDays((p) => ({ ...p, [day]: !p[day] }))}
                      className="flex items-center gap-2 text-left flex-1 min-w-0"
                      data-testid={`log-day-toggle-${day}`}
                      aria-expanded={isOpen}
                    >
                      {isOpen ? (
                        <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />
                      ) : (
                        <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
                      )}
                      <span className="text-sm font-medium">
                        {fmtDay(day)}{" "}
                        <span className="font-normal text-muted-foreground">
                          · {entries.length} {entries.length === 1 ? "Löschung" : "Löschungen"}
                        </span>
                      </span>
                    </button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        downloadLogCsv(day);
                      }}
                      className="rounded-none h-8 shrink-0"
                      data-testid={`log-download-day-${day}`}
                    >
                      <Download className="h-3.5 w-3.5 mr-1.5" />
                      CSV
                    </Button>
                  </div>
                  {isOpen && (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-border text-left text-muted-foreground">
                            <th className="px-4 py-2.5 font-medium">Zeit</th>
                            <th className="px-4 py-2.5 font-medium">Firma</th>
                            <th className="px-4 py-2.5 font-medium">Name</th>
                            <th className="px-4 py-2.5 font-medium">E-Mail</th>
                            <th className="px-4 py-2.5 font-medium text-right">Belege</th>
                            <th className="px-4 py-2.5 font-medium text-right">Gesamtbetrag</th>
                            <th className="px-4 py-2.5 font-medium">Admin</th>
                            <th className="px-4 py-2.5 font-medium text-right">Export</th>
                          </tr>
                        </thead>
                        <tbody>
                          {entries.map((e) => (
                            <tr
                              key={e.id}
                              className="border-b border-border/60 last:border-0"
                              data-testid={`log-row-${e.id}`}
                            >
                              <td className="px-4 py-3 tabular text-muted-foreground">{fmtTime(e.deleted_at)}</td>
                              <td className="px-4 py-3 font-medium">{e.company || "—"}</td>
                              <td className="px-4 py-3">{e.name || "—"}</td>
                              <td className="px-4 py-3 text-muted-foreground">{e.email}</td>
                              <td className="px-4 py-3 text-right tabular">{e.receipt_count}</td>
                              <td className="px-4 py-3 text-right tabular">
                                {formatMoney(e.total_amount || 0, "EUR")}
                              </td>
                              <td className="px-4 py-3 text-muted-foreground">{e.admin_email || "—"}</td>
                              <td className="px-4 py-3 text-right">
                                {e.export_available && e.download_url ? (
                                  <a
                                    href={e.download_url}
                                    className="inline-flex items-center gap-1.5 text-primary hover:underline font-medium"
                                    data-testid={`log-export-${e.id}`}
                                  >
                                    <Download className="h-3.5 w-3.5" />
                                    Export
                                  </a>
                                ) : (
                                  <span className="text-xs text-muted-foreground">abgelaufen</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
